import type { WidgetConfig } from '@/types/config';
import { ConfigParser } from './ConfigParser';
import { EventBus } from './EventBus';
import { Button } from '@/components/Button';
import { OverlayMode } from '@/modes/OverlayMode';
import { InlineMode } from '@/modes/InlineMode';
import { RedirectMode } from '@/modes/RedirectMode';
import type { BaseMode } from '@/modes/BaseMode';

/**
 * Main Widget class - orchestrates widget initialization and lifecycle
 */
export class Widget {
  private config: WidgetConfig;
  private eventBus: EventBus;
  private buttonComponent: Button;
  private modeHandler: BaseMode;

  constructor(private buttonElement: HTMLButtonElement) {
    // Parse configuration from button attributes
    this.config = ConfigParser.parse(buttonElement);

    // Create EventBus for PostMessage communication
    this.eventBus = new EventBus(this.config);

    // Create button component for styling
    this.buttonComponent = new Button(buttonElement, this.config);

    // Create appropriate mode handler based on configuration
    this.modeHandler = this.createModeHandler();
  }

  /**
   * Factory method to create the appropriate mode handler
   */
  private createModeHandler(): BaseMode {
    switch (this.config.mode) {
      case 'overlay':
        return new OverlayMode(this.config, this.eventBus, this.buttonElement);

      case 'inline':
      case 'inline-simple':
        return new InlineMode(this.config, this.eventBus, this.buttonElement);

      case 'redirect':
      case 'new-tab':
        return new RedirectMode(this.config, this.eventBus, this.buttonElement);

      default:
        console.warn(`[CaredoveWidget] Unknown mode: ${this.config.mode}, defaulting to overlay`);
        return new OverlayMode(this.config, this.eventBus, this.buttonElement);
    }
  }

  /**
   * Initialize the widget
   */
  public initialize(): void {
    try {
      // Apply button styles
      this.buttonComponent.applyStyles();

      // Start listening to PostMessage events
      this.eventBus.listen();

      // Render the mode
      this.modeHandler.render();

      console.log(`[CaredoveWidget] Widget initialized in ${this.config.mode} mode`);
    } catch (error) {
      console.error('[CaredoveWidget] Error initializing widget:', error);
      throw error;
    }
  }

  /**
   * Destroy the widget and cleanup
   */
  public destroy(): void {
    try {
      // Cleanup mode handler
      this.modeHandler.cleanup();

      // Stop listening to PostMessage events
      this.eventBus.destroy();

      // Remove button styles
      this.buttonComponent.removeStyles();

      console.log('[CaredoveWidget] Widget destroyed');
    } catch (error) {
      console.error('[CaredoveWidget] Error destroying widget:', error);
    }
  }

  /**
   * Get widget configuration
   */
  public getConfig(): WidgetConfig {
    return { ...this.config };
  }

  /**
   * Get the button element
   */
  public getButton(): HTMLButtonElement {
    return this.buttonElement;
  }

  /**
   * Check if widget is listening to PostMessage events
   */
  public isListening(): boolean {
    return this.eventBus.listening;
  }
}
